/**
 * Dust motes drifting through the hero: a 2D canvas, capped at 1.5x DPR,
 * paused when off-screen or the tab is hidden. Pointer parallax on desktop.
 * Cheap enough for mobile Safari: a few dozen arcs per frame, nothing else.
 */

type Mote = {
  x: number;
  y: number;
  r: number;
  a: number;
  vx: number;
  vy: number;
  ph: number;
  depth: number;
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export function initDust(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const host = canvas.parentElement ?? document.body;

  let w = 1;
  let h = 1;
  let motes: Mote[] = [];
  let raf: number | null = null;
  let visible = false;
  let last = 0;
  const target = { x: 0, y: 0 };
  const cur = { x: 0, y: 0 };

  const spawn = (anywhere: boolean): Mote => {
    const depth = rand(0.3, 1);
    return {
      x: rand(0, w),
      y: anywhere ? rand(0, h) : h + 10,
      r: rand(0.5, 1.6) * depth + 0.35,
      a: rand(0.15, 0.7) * depth,
      vx: rand(-4, 4) * depth,
      vy: rand(-13, -3) * depth,
      ph: rand(0, Math.PI * 2),
      depth
    };
  };

  const draw = (dt: number) => {
    ctx.clearRect(0, 0, w, h);
    cur.x += (target.x - cur.x) * 0.04;
    cur.y += (target.y - cur.y) * 0.04;
    const t = performance.now() / 1000;

    for (let i = 0; i < motes.length; i++) {
      const m = motes[i];
      m.x += (m.vx + Math.sin(t * 0.6 + m.ph) * 3) * dt;
      m.y += m.vy * dt;
      if (m.y < -10 || m.x < -10 || m.x > w + 10) {
        motes[i] = spawn(false);
        continue;
      }
      const px = m.x + cur.x * 18 * m.depth;
      const py = m.y + cur.y * 18 * m.depth;
      const twinkle = 0.75 + 0.25 * Math.sin(t * 1.7 + m.ph * 3);
      ctx.globalAlpha = m.a * twinkle;
      ctx.fillStyle = m.depth > 0.75 ? '#dff6ff' : '#8fd3ea';
      ctx.beginPath();
      ctx.arc(px, py, m.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  const resize = () => {
    const rect = host.getBoundingClientRect();
    w = Math.max(1, Math.round(rect.width));
    h = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const count = Math.round(Math.min(110, Math.max(36, (w * h) / 14000)));
    if (motes.length > count) motes.length = count;
    while (motes.length < count) motes.push(spawn(true));
    if (reduce) draw(0);
  };

  const loop = (now: number) => {
    raf = null;
    if (!visible) return;
    const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
    last = now;
    draw(dt);
    raf = requestAnimationFrame(loop);
  };

  const start = () => {
    if (raf !== null || reduce) return;
    last = performance.now();
    raf = requestAnimationFrame(loop);
  };

  const stop = () => {
    if (raf === null) return;
    cancelAnimationFrame(raf);
    raf = null;
  };

  resize();

  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(resize).observe(host);
  } else {
    window.addEventListener('resize', resize);
  }

  if (typeof IntersectionObserver === 'function') {
    new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting);
        if (visible) start();
        else stop();
      },
      { threshold: 0.01 }
    ).observe(host);
  } else {
    visible = true;
    start();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else if (visible) start();
  });

  if (fine && !reduce) {
    window.addEventListener(
      'pointermove',
      (e) => {
        target.x = (e.clientX / window.innerWidth - 0.5) * 2;
        target.y = (e.clientY / window.innerHeight - 0.5) * 2;
      },
      { passive: true }
    );
  }
}
